import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Formik, Form as FormikForm, useField, useFormikContext } from "formik";
import {
  Box,
  Button,
  Checkbox as MUICheckbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  TextField as MUITextField,
  Typography,
} from "@mui/material";

/* ---------------- persistence helpers ---------------- */
const persist = (name: string, value: any) => {
  try {
    localStorage.setItem(name, JSON.stringify(value ?? ""));
  } catch {}
};
const handleBlurToLS = (e: any) => {
  if (e?.target?.name !== undefined) persist(e.target.name, e.target.value);
};

/** Local option type so this file compiles on its own */
type Option = { key: string | number; value: string; label?: string };

/** Accept either Option[] or string[] and normalize */
const normalizeOptions = (opts?: Option[] | string[]): Option[] =>
  (opts ?? []).map((o, i) =>
    typeof o === "string" ? { key: i, value: o, label: o } : o,
  );

/* ---------------- base Form wrapper ---------------- */
export function Form(props: any) {
  return (
    <Formik {...props}>
      <FormikForm noValidate>{props.children}</FormikForm>
    </Formik>
  );
}

/* ---------------- title/label block ---------------- */
export function FormHeader(props: {
  label?: string;
  required?: boolean;
  claimNumber?: string;
  description?: string;
}) {
  const { label, required, claimNumber, description } = props;
  if (!label && !claimNumber) return null;

  return (
    <Box mb={2} textAlign="center">
      {label && (
        <Typography variant="h5" component="div">
          {label}
          {required && <span className="asterisk_input" />}
        </Typography>
      )}
      {description && (
        <Box
          dangerouslySetInnerHTML={{
            __html: description || "",
          }}
          sx={{
            "& p": { mb: 1 },
            "& h1, & h2, & h3": { mt: 2, mb: 1 },
            "& ul, & ol": { pl: 3, mb: 1 },
            "& a": { color: "secondary.main" },
          }}
        />
      )}
    </Box>
  );
}

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children?: React.ReactNode;
  required?: boolean;
}) {
  if (!children) return null;
  return (
    <label htmlFor={htmlFor} style={{ display: "block", marginBottom: 4 }}>
      {children}
      {required && <span style={{ color: "#d32f2f", marginLeft: 4 }}>*</span>}
    </label>
  );
}

/* ---------------- Text field ---------------- */
export function TextField(props: {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const [field, meta] = useField(props.name);
  const hasError = Boolean(meta.touched && meta.error);
  return (
    <>
      <FieldLabel htmlFor={props.id || props.name} required={props.required}>
        {props.label}
      </FieldLabel>
      <MUITextField
        fullWidth
        variant="outlined"
        size="medium"
        id={props.id || props.name}
        placeholder={props.placeholder || ""}
        required={props.required}
        error={hasError}
        helperText={hasError ? meta.error : " "}
        {...field}
        onBlur={(e) => {
          field.onBlur(e);
          handleBlurToLS(e);
        }}
        inputProps={{ "aria-labelledby": props.id || props.name }}
      />
    </>
  );
}

/* ---------------- TextArea ---------------- */
export function TextArea(props: {
  id?: string;
  name: string;
  label?: string;
  required?: boolean;
}) {
  const [field, meta] = useField(props.name);
  const hasError = Boolean(meta.touched && meta.error);
  return (
    <>
      <FieldLabel htmlFor={props.id || props.name} required={props.required}>
        {props.label}
      </FieldLabel>
      <MUITextField
        fullWidth
        multiline
        minRows={4}
        variant="outlined"
        size="medium"
        id={props.id || props.name}
        required={props.required}
        error={hasError}
        helperText={hasError ? meta.error : " "}
        {...field}
        onBlur={(e) => {
          field.onBlur(e);
          handleBlurToLS(e);
        }}
        inputProps={{ "aria-labelledby": props.id || props.name }}
      />
    </>
  );
}

/* ---------------- Select ---------------- */

export function SelectField(props: {
  id?: string;
  name: string;
  label?: string;
  options?: Option[] | string[];
  required?: boolean;
  placeholder?: string; // default text when empty
}) {
  const [field, meta, helpers] = useField<string>(props.name);
  const hasError = Boolean(meta.touched && meta.error);
  const id = props.id || props.name;
  const options = normalizeOptions(props.options);
  const placeholderText = props.placeholder ?? "None";

  // use label if available (matches how we read from LS), otherwise fall back to name
  const storageKey = props.label ?? props.name;

  return (
    <>
      <FieldLabel htmlFor={id} required={props.required}>
        {props.label}
      </FieldLabel>

      <FormControl fullWidth error={hasError}>
        <Select
          id={id}
          value={field.value ?? ""}
          onChange={(e) => {
            const val = (e.target as HTMLSelectElement).value;
            helpers.setValue(val);

            // cache selected value so initForm/readCached() can restore it
            persist(storageKey, val);
          }}
          onBlur={(e) => {
            field.onBlur(e);
            // make sure the last value is persisted even if it didn’t change
            persist(storageKey, field.value ?? "");
          }}
          displayEmpty
          inputProps={{ name: field.name, id, autoComplete: "off" }}
          renderValue={(selected) =>
            selected === "" ? <em>{placeholderText}</em> : (selected as string)
          }
        >
          <MenuItem value="" disabled={!!props.required}>
            <em>{placeholderText}</em>
          </MenuItem>

          {options.map((o) => (
            <MenuItem key={o.key} value={o.value}>
              {o.label ?? o.value}
            </MenuItem>
          ))}
        </Select>

        <FormHelperText>{hasError ? meta.error : " "}</FormHelperText>
      </FormControl>
    </>
  );
}

/* ---------------- Checkbox ---------------- */
export function Checkbox(props: {
  id?: string;
  name: string;
  label?: string; // used as LS key everywhere else
  required?: boolean;
  disabled?: boolean;
}) {
  const [field, meta, helpers] = useField({
    name: props.name,
    type: "checkbox",
  });
  const hasError = Boolean(meta.touched && meta.error);
  const id = props.id || props.name;

  // use the same key scheme as the rest of your form (label first, then name)
  const storageKey = props.label ?? props.name;
  const checked = field.value === true; // ensure a boolean

  return (
    <FormControl error={hasError} component="fieldset" variant="standard">
      <FormControlLabel
        label={
          <span id={`${id}-label`}>
            {props.label}
            {props.required && (
              <span style={{ color: "#d32f2f", marginLeft: 4 }}>*</span>
            )}
          </span>
        }
        control={
          <MUICheckbox
            id={id}
            checked={checked}
            onChange={(e) => {
              const next = e.target.checked;
              helpers.setValue(next);
              try {
                localStorage.setItem(storageKey, JSON.stringify(next));
              } catch {}
            }}
            onBlur={field.onBlur} // mark as touched so validation shows
            inputProps={{ "aria-labelledby": `${id}-label` }}
            disabled={props.disabled}
          />
        }
      />
      <FormHelperText>{hasError ? meta.error : " "}</FormHelperText>
    </FormControl>
  );
}

/* ---------------- DatePicker (react-datepicker + MUI TextField) ---------------- */
const MUITextFieldInput = forwardRef<HTMLInputElement, any>(function MuiDP(
  { error, helperText, required, placeholder, ...rest },
  ref,
) {
  return (
    <MUITextField
      fullWidth
      variant="outlined"
      size="medium"
      required={required}
      error={error}
      helperText={helperText || " "}
      placeholder={placeholder}
      inputRef={ref}
      {...rest}
    />
  );
});

/* ---------------- Strict NumberField (integer/decimal) ---------------- */
export function NumberField(props: {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  decimals?: number; // 0 = integer (default), >0 allows that many decimals
  allowNegative?: boolean; // default false
}) {
  const {
    id,
    name,
    label,
    placeholder,
    required,
    min,
    max,
    decimals = 0,
    allowNegative = false,
  } = props;

  const [field, , helpers] = useField(name);
  const inputId = id || name;

  // We keep a string for the live input (so partial values like "1." are allowed),
  // but only commit a Number (or "") to Formik on blur.
  const display =
    typeof field.value === "number" ? String(field.value) : (field.value ?? "");

  const reInteger = allowNegative ? /^-?\d*$/ : /^\d*$/;
  const reDecimal = new RegExp(
    `^${allowNegative ? "-?" : ""}(?:\\d+)?(?:[\\.,]\\d{0,${decimals}})?$`,
  );

  const isAllowed = (s: string) => {
    if (s === "") return true;
    return decimals > 0 ? reDecimal.test(s) : reInteger.test(s);
  };

  const clamp = (n: number) => {
    let v = n;
    if (typeof min === "number") v = Math.max(v, min);
    if (typeof max === "number") v = Math.min(v, max);
    return v;
  };

  const toNumberOrEmpty = (s: string) => {
    if (s.trim() === "" || s === "-" || s === "." || s === ",") return "";
    const normalized = s.replace(",", "."); // support , as decimal sep
    const n = Number(normalized);
    return Number.isFinite(n) ? clamp(n) : "";
  };

  return (
    <>
      <FieldLabel htmlFor={inputId} required={required}>
        {label}
      </FieldLabel>

      <MUITextField
        fullWidth
        variant="outlined"
        size="medium"
        id={inputId}
        // IMPORTANT: use text, not number, to fully control allowed chars
        type="text"
        placeholder={placeholder || ""}
        value={display}
        onChange={(e) => {
          const s = e.target.value;
          if (isAllowed(s)) {
            helpers.setValue(s); // keep as string while editing
          }
        }}
        onBeforeInput={(e: any) => {
          // extra guard for some IME/composition cases
          const next =
            display.slice(0, e.target.selectionStart) +
            e.data +
            display.slice(e.target.selectionEnd);
          if (!isAllowed(next)) e.preventDefault();
        }}
        onPaste={(e) => {
          const text = (e.clipboardData.getData("text") || "").trim();
          const next = text;
          if (!isAllowed(next)) e.preventDefault();
        }}
        onBlur={(e) => {
          // commit as Number | "" to Formik, and persist
          const committed = toNumberOrEmpty(e.target.value);
          helpers.setValue(committed);
          handleBlurToLS({ target: { name, value: committed } });
        }}
        inputProps={{
          "aria-labelledby": inputId,
          inputMode: decimals > 0 ? "decimal" : "numeric",
          pattern:
            decimals > 0
              ? `[0-9${allowNegative ? "\\-" : ""}${
                  decimals > 0 ? "[\\.,]" : ""
                }]*`
              : "[0-9]*",
        }}
      />
    </>
  );
}

/* ---------------- TimePicker (show Date, store "HH:mm") ---------------- */
export function TimePickerField(props: {
  id?: string;
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string; // e.g. "HH:mm"
  timeIntervals?: number; // minutes between times, default 15
}) {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta] = useField(props.name);
  const hasError = Boolean(meta.touched && meta.error);
  const id = props.id || props.name;

  // Build a Date for the picker from the stored value (string "HH:mm" | "" | Date | null)
  const toDateFromHHmm = (s: string): Date | null => {
    const m = /^(\d{2}):(\d{2})$/.exec(s);
    if (!m) return null;
    const h = Number(m[1]),
      min = Number(m[2]);
    if (h < 0 || h > 23 || min < 0 || min > 59) return null;
    const d = new Date();
    d.setHours(h, min, 0, 0);
    return d;
  };

  let selected: Date | null = null;
  if (typeof field.value === "string") {
    selected = field.value ? toDateFromHHmm(field.value) : null;
  } else if (field.value instanceof Date) {
    // legacy safety
    selected = isNaN(field.value.getTime()) ? null : field.value;
  } else {
    selected = null;
  }

  const toHHmm = (d: Date | null) =>
    d
      ? `${String(d.getHours()).padStart(2, "0")}:${String(
          d.getMinutes(),
        ).padStart(2, "0")}`
      : "";

  return (
    <>
      <FieldLabel htmlFor={id} required={props.required}>
        {props.label}
      </FieldLabel>
      <DatePicker
        selected={selected}
        onChange={(val: Date | null) => {
          const hhmm = toHHmm(val); // store "HH:mm"
          setFieldValue(field.name, hhmm);
          persist(field.name, hhmm);
        }}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={props.timeIntervals ?? 15}
        timeCaption="Time"
        timeFormat="HH:mm"
        dateFormat="HH:mm"
        wrapperClassName="customDatePicker"
        customInput={
          <MUITextFieldInput
            id={id}
            required={props.required}
            error={hasError}
            fullWidth
            helperText={hasError ? meta.error : " "}
            placeholder={props.placeholder ?? "HH:mm"}
            autoComplete="off"
            spellCheck={false}
            inputProps={{ "aria-labelledby": id, autoComplete: "off" }}
          />
        }
      />
    </>
  );
}

/* ---------------- DatePicker (store "YYYY-MM-DD" string, no timezone bugs) ---------------- */
export function DatePickerField(props: {
  id?: string;
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  [key: string]: any;
}) {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta] = useField(props.name);
  const hasError = Boolean(meta.touched && meta.error);
  const id = props.id || props.name;

  // helpers: format/parse date-only
  const toYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromYMD = (s: string) => {
    // construct with local time (no UTC shift), use noon to be extra safe
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
    return isNaN(dt.getTime()) ? null : dt;
  };
  const fromDMY = (s: string) => {
    const m = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/.exec(s.trim());
    if (!m) return null;
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    if (
      !Number.isFinite(day) ||
      !Number.isFinite(month) ||
      !Number.isFinite(year) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
    return isNaN(dt.getTime()) ? null : dt;
  };

  // Build the Date for the picker from the form value (string | Date | null)
  let selected: Date | null = null;
  if (field.value instanceof Date) {
    // legacy: if someone still sets a Date in the form state
    selected = isNaN(field.value.getTime()) ? null : field.value;
  } else if (typeof field.value === "string" && field.value) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(field.value)) {
      // our new stored format
      selected = fromYMD(field.value);
    } else {
      // legacy formats (dd/MM/yyyy) and ISO fallback
      selected = fromDMY(field.value);
      if (!selected) {
        const tmp = new Date(field.value);
        selected = isNaN(tmp.getTime()) ? null : tmp;
      }
    }
  } else {
    selected = null;
  }

  return (
    <>
      <FieldLabel htmlFor={id} required={props.required}>
        {props.label}
      </FieldLabel>
      <DatePicker
        selected={selected}
        onChange={(val: Date | null) => {
          const ymd = val ? toYMD(val) : ""; // <-- store as "YYYY-MM-DD"
          setFieldValue(field.name, ymd);
          persist(field.name, ymd);
        }}
        dateFormat="dd/MM/yyyy"
        showYearDropdown
        todayButton="Today"
        isClearable
        wrapperClassName="customDatePicker"
        customInput={
          <MUITextFieldInput
            id={id}
            required={props.required}
            error={hasError}
            fullWidth
            helperText={hasError ? meta.error : " "}
            placeholder={props.placeholder ?? "dd/mm/yyyy"}
            autoComplete="off"
            spellCheck={false}
            inputProps={{ "aria-labelledby": id, autoComplete: "off" }}
          />
        }
        {...props}
      />
    </>
  );
}

export function SubmitButton(
  props: { title: string } & React.ComponentProps<typeof Button>,
) {
  const { isSubmitting } = useFormikContext<any>();
  const { title, ...rest } = props;

  return (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      fullWidth
      size="large"
      disabled={isSubmitting}
      {...rest}
    >
      <strong>{title}</strong>
    </Button>
  );
}
